import React, { FC, Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Divider, Paper, Typography } from '@mui/material';
import { ActionOrderContext } from 'pages/order-flow/action-order/action-order-context';
import OrderFiles from 'components/order-flow/shared/order-files/order-files.component';
import { File as DenthubFile } from 'models/file';
import Comments from './comments/comments.component';
import Constructions from './constructions/constructions.component';
import { LoadingButton } from '@mui/lab';
import { permissions } from 'utils/permission';
import { currentUserVar } from 'apollo/reactive-variables/user';
import AttachedScannerCases from './scanner-cases/attached-scanner-cases.component';
import { RequiredAsterisk } from 'components/ui/required-asteriks';
import { useConstructions } from 'context/construction-context';

type ConstructionsStepProps = {
    onBackClick: () => void;
    onNextClick: () => void;
};
const ConstructionsStep: FC<ConstructionsStepProps> = ({ onBackClick, onNextClick }) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const { order, setOrder } = useContext(ActionOrderContext);
    const { saveDraftConstructions, isLoading, isNextBtnDisabled, isCommentRequired, isThreeShapeEnabled } =
        useConstructions();
    const [isFileUploading, setIsFileUploading] = useState(false);

    const currentUser = currentUserVar();

    const handleFilesChange = (files: DenthubFile[]) => {
        setOrder((prev) => ({ ...prev, files }));
    };

    return (
        <Fragment>
            <Box>
                <Box mb={2}>
                    <Typography variant="h1">{translate('orders:CONSTRUCTIONS_FILES')}</Typography>
                </Box>
                <Box mb={2}>
                    <Divider />
                </Box>

                <Box mb={4}>
                    <Paper variant="outlined">
                        <Box p={4}>
                            <Constructions />
                        </Box>
                    </Paper>
                </Box>
                <Box mb={4}>
                    <Box display="flex">
                        <Typography variant="h3" mb={1}>
                            {translate('orders:COMMENT')}
                        </Typography>
                        {isCommentRequired ? <RequiredAsterisk /> : undefined}
                    </Box>
                    <Box>
                        <Comments isCommentRequired={isCommentRequired} />
                    </Box>
                </Box>
                {isThreeShapeEnabled && permissions.isDentist(currentUser.type) ? (
                    <Box mb={4}>
                        <Box mb={2}>
                            <Typography variant="h3" gutterBottom>
                                {translate('orders:ATTACH_INTRAORAL_SCAN')}
                            </Typography>
                        </Box>
                        <AttachedScannerCases />
                    </Box>
                ) : undefined}
                <Box mb={4}>
                    <Box mb={2}>
                        <Typography variant="h3" gutterBottom>
                            {translate('common:FILES')}
                        </Typography>
                    </Box>
                    <OrderFiles
                        showDrawImageButton={true}
                        order={order}
                        onFilesChanged={handleFilesChange}
                        onUploadingChange={setIsFileUploading}
                    />
                </Box>
                <Box display="flex" mt={2} flexDirection="column" gap={2}>
                    {isFileUploading && (
                        <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                            {translate('orders:FILE_UPLOAD_IN_PROGRESS')}
                        </Alert>
                    )}
                    <Box display="flex" mt={2}>
                        <Box flexGrow={1} textAlign="left">
                            <Button type="button" onClick={onBackClick}>
                                {translate('common:BACK')}
                            </Button>
                        </Box>
                        <Box>
                            {((order._id && !order.isDraft) || permissions.isLabTechnician(currentUser.type)) && (
                                <Button
                                    disabled={isNextBtnDisabled || isFileUploading}
                                    type="button"
                                    onClick={onNextClick}
                                    variant="contained"
                                >
                                    {translate('common:NEXT_STEP')}
                                </Button>
                            )}
                            {order.isDraft && !permissions.isLabTechnician(currentUser.type) && (
                                <>
                                    <Button
                                        disabled={isNextBtnDisabled || isFileUploading}
                                        type="button"
                                        onClick={async () => await saveDraftConstructions()}
                                        sx={{ mr: 2 }}
                                    >
                                        {translate('common:SAVE_DRAFT')}
                                    </Button>
                                    <LoadingButton
                                        loading={isLoading}
                                        variant="contained"
                                        disabled={isNextBtnDisabled || isFileUploading}
                                        type="button"
                                        onClick={async () => {
                                            await saveDraftConstructions();
                                            onNextClick();
                                        }}
                                        data-test="construction-step.draft-submit"
                                    >
                                        {translate('common:SAVE_AND_NEXT')}
                                    </LoadingButton>
                                </>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Fragment>
    );
};

export default ConstructionsStep;
